import { AgGridChoiceFilterAdapter, AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { MtrStatusValue, MtrTypeValue, SupplyByValue } from 'constantsProject';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { SortEnumType, type ContractStatusEnum, type PriceListMtrRowData, type ProjectData } from 'gql/graphql';
import { getDefaultFilterCleaner, usePageFilterSaver, withDefaultTransformer } from 'hooks/usePageFilterSaver';
import { getYesNoStatus } from 'utils/getYesNoTableStatus';
import { sortItemsFilterWithNullable } from 'utils/sortItemsFilterWithNulleble';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { useCallback, useMemo, useState } from 'react';

import { MtrCodeCellRendererComponent } from './cellRenderers';
import {
    priceListMtrCodeFilter,
    priceListMtrJustificationFilter,
    priceListMtrKeyFilter,
    priceListMtrNameFilter,
    priceListMtrStatusFilter,
    priceListMtrSupplyByFilter,
    priceListMtrTypeFilter,
} from './mtrQueries';

interface IUsePriceListColumns {
    contractId: ProjectData['contractId'];
    contractStatus: ContractStatusEnum | undefined;
}

const MenuCellStyle = { padding: 0 };

const keyItemsFilterWithNullable = (items: { key: string | null }[]) => {
    if (!items) {
        return null;
    }

    let nullableItem;

    for (let i = 0; i < items.length - 1; i++) {
        if (!items[i]?.key) {
            nullableItem = {
                key: '<Нет значения>',
                value: null,
            };

            break;
        }
    }

    if (!nullableItem) {
        return items;
    }

    return [...items.filter(({ key }) => key), nullableItem];
};

export const usePriceListColumns = ({ contractId, contractStatus }: IUsePriceListColumns) => {
    const [resetStatusFilter] = useResetStoreData({
        requestId: 'uerPriceListMtrStatusFilter',
    });

    const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});

    const variables = useMemo(() => ({ contractId }), [contractId]);

    const filterVariablesName = useMemo(
        () => ({
            contractId,
            order: { value: SortEnumType.Asc },
        }),
        [contractId],
    );

    const changeFilterHandler = useCallback<(params: { fieldName: string; checkedValues: string[] }) => void>(
        ({ fieldName, checkedValues }) => {
            const filterValueArr =
                fieldName === 'code' ? checkedValues.map((v: string | null) => v ?? 'Новый') : checkedValues;

            setFilterValues((prevValue) => ({
                ...prevValue,
                [fieldName]: filterValueArr,
            }));
        },
        [],
    );

    usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: withDefaultTransformer(
                    changeFilterHandler,
                    'mtrList',
                    getDefaultFilterCleaner({
                        changeFilterHandler,
                        setFiltersWhere: setFilterValues,
                    }),
                ),
            }),
            [changeFilterHandler],
        ),
    );

    const columns = useMemo<(ColDef<PriceListMtrRowData> | ColGroupDef<PriceListMtrRowData>)[]>(
        () => [
            {
                field: 'menu' as any,
                headerName: '',
                width: 48,
                pinned: 'left',
                lockPosition: 'left',
                cellStyle: MenuCellStyle,
                resizable: false,
                cellRenderer: MtrCodeCellRendererComponent,
                cellRendererParams: {
                    contractStatus,
                },
            },
            {
                field: 'code',
                headerName: 'Шифр',
                width: 180,
                lockPosition: 'left',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    dataAccessor: 'uerPriceListMtrCodeFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'value',
                            checkboxSelection: true,
                            cellRenderer: ({ value }: { value: string }) => value || '<Новый>',
                            width: 400,
                        },
                    ],
                    query: priceListMtrCodeFilter,
                },
            },
            {
                field: 'name',
                headerName: 'Наименование',
                minWidth: 400,
                flex: 1,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    changeFilterHandler,
                    dataAccessor: 'uerPriceListMtrNameFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'value',
                            checkboxSelection: true,
                            cellRenderer: ({ value }: { value: string }) => value || '<Новый>',
                            width: 400,
                        },
                    ],
                    variables: filterVariablesName,
                    query: priceListMtrNameFilter,
                },
            },
            {
                field: 'unit',
                headerName: 'Ед.изм.',
                width: 90,
            },
            {
                field: 'volume',
                headerName: 'Объем',

                hide: true,
            },
            {
                field: 'unitWeight',
                headerName: 'Вес единицы, т',
                width: 141,
            },
            {
                field: 'unitCost',
                headerName: 'Стоимость единицы, руб без НДС',
                width: 180,
                cellClass: 'ag-right-aligned-cell',
                valueFormatter: ({ value }) =>
                    typeof value === 'number'
                        ? new Intl.NumberFormat('ru', { minimumFractionDigits: 2 }).format(value)
                        : '',
            },
            {
                field: 'supplyBy',
                headerName: 'Поставка',
                width: 125,
                valueFormatter: ({ value }) => SupplyByValue[value] ?? '',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    dataAccessor: 'uerPriceListMtrSupplyByFilter',
                    withSearch: false,
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Поставка',
                            field: 'value',
                            checkboxSelection: true,
                            cellRenderer: ({ value }: { value: string }) => SupplyByValue[value],
                            width: 400,
                        },
                    ],
                    query: priceListMtrSupplyByFilter,
                },
            },
            {
                field: 'mtrType',
                headerName: 'Тип',
                width: 120,
                valueFormatter: ({ value }) => MtrTypeValue[value] ?? '',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    dataAccessor: 'uerPriceListMtrTypeFilter',
                    withSearch: false,
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Тип',
                            field: 'value',
                            checkboxSelection: true,
                            cellRenderer: ({ value }: { value: string }) => MtrTypeValue[value] ?? '',
                            width: 400,
                        },
                    ],
                    query: priceListMtrTypeFilter,
                },
            },
            {
                field: 'status',
                headerName: 'Статус записи',
                width: 150,
                valueFormatter: ({ value }) => MtrStatusValue[value] ?? '',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    dataAccessor: 'uerPriceListMtrStatusFilter',
                    withSearch: false,
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Статус записи',
                            field: 'value',
                            checkboxSelection: true,
                            cellRenderer: ({ value }: { value: string }) => MtrStatusValue[value],
                            width: 400,
                        },
                    ],
                    query: priceListMtrStatusFilter,
                },
            },
            {
                field: 'key',
                headerName: 'Код',
                width: 120,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    dataAccessor: 'uerPriceListMtr',
                    withSearch: true,
                    whereVariables: ['key'],
                    childFieldAccessor: 'key',
                    columns: [
                        {
                            headerName: 'Код',
                            field: 'key',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: priceListMtrKeyFilter,
                    groupAndSortItemsHelper: keyItemsFilterWithNullable,
                },
            },
            {
                headerName: 'Используется',
                marryChildren: true,
                children: [
                    {
                        field: 'isIncludedInWorkOrder',
                        headerName: 'НЗ',
                        width: 105,
                        valueFormatter: ({ value }) => getYesNoStatus(value),
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                    {
                        field: 'isIncludedInAct',
                        headerName: 'Акт',
                        width: 105,
                        valueFormatter: ({ value }) => getYesNoStatus(value),
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                    {
                        field: 'isIncludedInWorkOrderChange',
                        headerName: 'Изм. к НЗ',
                        headerClass: 'formatTitleWidth',
                        width: 105,
                        valueFormatter: ({ value }) => getYesNoStatus(value),
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                ],
            },
            {
                field: 'justification',
                headerName: 'Обоснование',
                minWidth: 155,
                flex: 1,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    dataAccessor: 'uerPriceListMtrJustificationFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Обоснование',
                            field: 'value',
                            checkboxSelection: true,
                            cellRenderer: ({ value }: { value: string }) => value || '<Нет значения>',
                            width: 400,
                        },
                    ],
                    query: priceListMtrJustificationFilter,
                    groupAndSortItemsHelper: sortItemsFilterWithNullable,
                },
            },
        ],
        [changeFilterHandler, contractStatus, filterVariablesName, variables],
    );

    return useMemo(() => ({ columns, filterValues, resetStatusFilter }), [columns, filterValues, resetStatusFilter]);
};
